// invoice_shapes RPC helpers — Phase 5 Migration B sub-track (c).
//
// Mirror of crewbit-mobile/lib/invoiceShapes.ts. invoice_shapes RPCs return
// raw values (uuid / jsonb / void / setof rows) rather than the
// {success,error} envelope callMoneyRpc enforces; direct supabase.rpc is
// correct here with explicit error normalization at call sites.

import { supabase } from './supabase';
import { linesFromCents, type RpcLineItem } from './invoiceLineItems';
import type { AuthoringDraftState } from '../hooks/useInvoiceDraft';
import type {
  InvoiceShape,
  InvoiceShapeApplyBlob,
  InvoiceShapeDraft,
} from '../types/invoiceShapes';

export type ShapeRpcResult<T> =
  | { success: true; data: T }
  | { success: false; error: string };

export async function saveInvoiceAsShape(
  draft: InvoiceShapeDraft,
): Promise<ShapeRpcResult<string>> {
  const { data, error } = await supabase.rpc('save_invoice_as_shape', {
    p_invoice_id: draft.invoiceId,
    p_name: draft.name,
  });
  if (error) return { success: false, error: error.message };
  if (typeof data !== 'string') {
    return { success: false, error: 'Unexpected response shape from save_invoice_as_shape' };
  }
  return { success: true, data };
}

export async function applyShapeToInvoiceDraft(
  shapeId: string,
): Promise<ShapeRpcResult<InvoiceShapeApplyBlob | null>> {
  const { data, error } = await supabase.rpc('apply_shape_to_invoice_draft', {
    p_shape_id: shapeId,
  });
  if (error) return { success: false, error: error.message };
  return { success: true, data: (data as InvoiceShapeApplyBlob | null) ?? null };
}

export async function archiveShape(
  shapeId: string,
): Promise<ShapeRpcResult<void>> {
  const { error } = await supabase.rpc('archive_shape', { p_shape_id: shapeId });
  if (error) return { success: false, error: error.message };
  return { success: true, data: undefined };
}

export async function listInvoiceShapes(): Promise<ShapeRpcResult<InvoiceShape[]>> {
  const { data, error } = await supabase.rpc('list_invoice_shapes');
  if (error) return { success: false, error: error.message };
  return { success: true, data: (data as InvoiceShape[] | null) ?? [] };
}

// Pure helper — derives the authoring-draft patch from an apply-shape blob.
// Caller is responsible for calling setState. Kept in /lib (not the panel) so
// MoneyInvoiceAuthoringPanel stays under Golden Rule #14's 500-line ceiling.
export function applyShapeBlobToDraft(
  prev: AuthoringDraftState,
  blob: InvoiceShapeApplyBlob,
): AuthoringDraftState {
  const rpcLines = (blob.line_items ?? []) as unknown as RpcLineItem[];
  const hydratedLines = rpcLines.length > 0 ? linesFromCents(rpcLines) : prev.lines;
  const next: AuthoringDraftState = {
    ...prev,
    lines: hydratedLines,
    notes:
      blob.notes !== null && blob.notes !== undefined && blob.notes.length > 0
        ? blob.notes
        : prev.notes,
    paymentTermsDaysOverride: blob.payment_terms_days,
  };
  if (blob.payment_terms_days !== null) {
    const issued = new Date(`${prev.issueDate}T00:00:00`);
    issued.setDate(issued.getDate() + blob.payment_terms_days);
    const yyyy = issued.getFullYear();
    const mm = String(issued.getMonth() + 1).padStart(2, '0');
    const dd = String(issued.getDate()).padStart(2, '0');
    next.dueDate = `${yyyy}-${mm}-${dd}`;
  }
  return next;
}
